/**
 * 求人（Workgate / Carat）の表示整形。`this` を参照しない純関数。
 *
 * もともと workgate-helpers-mixin に置かれていたが、Gate A が新規コードからの
 * mixin import を禁じているため、Composition API のコンポーネントからも
 * 使えるようここへ切り出した。mixin 側は同じ実装をここへ委譲している。
 */

const PREF_NAMES = [
  '北海道',
  '青森県',
  '岩手県',
  '宮城県',
  '秋田県',
  '山形県',
  '福島県',
  '茨城県',
  '栃木県',
  '群馬県',
  '埼玉県',
  '千葉県',
  '東京都',
  '神奈川県',
  '新潟県',
  '富山県',
  '石川県',
  '福井県',
  '山梨県',
  '長野県',
  '岐阜県',
  '静岡県',
  '愛知県',
  '三重県',
  '滋賀県',
  '京都府',
  '大阪府',
  '兵庫県',
  '奈良県',
  '和歌山県',
  '鳥取県',
  '島根県',
  '岡山県',
  '広島県',
  '山口県',
  '徳島県',
  '香川県',
  '愛媛県',
  '高知県',
  '福岡県',
  '佐賀県',
  '長崎県',
  '熊本県',
  '大分県',
  '宮崎県',
  '鹿児島県',
  '沖縄県'
]

type Item = Record<string, any> | null | undefined

/** 勤務地。pref_name/city_name 形式と pref.name/city.name 形式の両方に対応する */
export function wgFormatLocation(item: Item): string {
  if (!item) return '-'
  const parts = []
  if (item.pref_name) parts.push(item.pref_name)
  if (item.city_name) parts.push(item.city_name)
  if (item.place) parts.push(item.place)
  if (parts.length > 0) return parts.join(' ')

  const parts2 = []
  if (item.pref && item.pref.name) parts2.push(item.pref.name)
  if (item.city && item.city.name) parts2.push(item.city.name)
  if (item.place) parts2.push(item.place)
  return parts2.length > 0 ? parts2.join(' ') : '-'
}

export function wgFormatSalary(item: Item): string {
  if (!item || !item.salary) return '-'
  const salary = item.salary
  if (salary.full_text) return salary.full_text
  if (salary.min != null && salary.max != null)
    return `${salary.min}〜${salary.max}円`
  if (salary.min != null) return `${salary.min}円〜`
  if (salary.max != null) return `〜${salary.max}円`
  if (salary.etc) return salary.etc
  return '-'
}

export function wgGetStationName(item: Item): string {
  if (!item) return '-'
  const names = []
  if (item.line && item.line.name) names.push(item.line.name)
  if (item.station && item.station.name) names.push(item.station.name)
  if (names.length > 0) return names.join(' / ')
  if (item.station_name) return item.station_name
  return '-'
}

export function wgGetAppealNames(item: any): any {
  if (!item) return []
  if (Array.isArray(item.appeal_name)) return item.appeal_name
  if (Array.isArray(item.appeals)) {
    return item.appeals
      .map((a: any) => (a && a.name ? a.name : ''))
      .filter(Boolean)
  }
  if (Array.isArray(item.appeal)) {
    return item.appeal
      .map((a: any) => (a && a.name ? a.name : ''))
      .filter(Boolean)
  }
  return []
}

export function wgNormalizeDigits(value: any): any {
  return String(value || '')
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/[^\d]/g, '')
}

export function wgFormatBirth(year: any, month: any, day: any): any {
  if (!year || !month || !day) return ''
  const toHalf = (v: any) =>
    String(v)
      .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
      .replace(/[^\d]/g, '')
  const y = toHalf(year)
  const m = toHalf(month)
  const d = toHalf(day)
  if (!y || !m || !d) return ''
  return `${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

export function wgBuildJobHistory(source: any): any {
  const histories = Array.isArray(source.jobHistories)
    ? source.jobHistories
    : []
  if (histories.length === 0) return null
  const lines = histories
    .map((h: any) => {
      const y = h && h.year ? `${h.year}年` : ''
      const m = h && h.month ? `${h.month}月` : ''
      const company = (h && (h.companyName || h.company || h.name)) || ''
      const detail = (h && (h.detail || h.text || h.description)) || ''
      return [y + m, company, detail].filter(Boolean).join(' ')
    })
    .filter(Boolean)
  if (lines.length === 0) return { textarea: '職歴あり' }
  return { textarea: lines.join('\n') }
}

export function wgBuildEducation(source: any): any {
  const histories = Array.isArray(source.educationalHistories)
    ? source.educationalHistories
    : []
  if (histories.length === 0) return null
  const lines = histories
    .map((h: any) => {
      const y = h && h.year ? `${h.year}年` : ''
      const m = h && h.month ? `${h.month}月` : ''
      const desc = h && h.description ? h.description : ''
      return [y + m, desc].filter(Boolean).join(' ')
    })
    .filter(Boolean)
  if (lines.length === 0) return null
  return { textarea: lines.join('\n') }
}

export function wgBuildIntroduction(source: any): any {
  const histories = Array.isArray(source.licenseHistories)
    ? source.licenseHistories
    : []
  const descriptions = histories
    .map((h: any) => (h && h.description ? h.description.trim() : ''))
    .filter(Boolean)
  if (descriptions.length === 0) return null
  return { textarea: '【所持資格】\n' + descriptions.join('\n') }
}

export function wgParsePrefecture(address: any): any {
  const normalized = String(address || '').trim()
  for (let i = 0; i < PREF_NAMES.length; i++) {
    if (normalized.indexOf(PREF_NAMES[i]) === 0) {
      return { code: i + 1, name: PREF_NAMES[i] }
    }
  }
  return { code: null, name: '' }
}
